/**
 * Public application form - /apply
 */

import { useState } from 'react';
import { buildApiUrl } from '../utils/api';

export default function Apply() {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    has_license: '',
    has_cdl_license: '',
    available_start: '',
    experience: '',
    source: '',
    sms_consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(buildApiUrl('/apply'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Application Received!
            </h2>
            <p className="text-gray-600 mb-4">
              Thank you for applying to 1st Choice Ambulette. We&rsquo;ll be in touch via text message soon.
            </p>
            <p className="text-sm text-gray-400">
              You may close this page.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 py-6 sm:py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            1st Choice Ambulette Service
          </h1>
          <p className="text-gray-600 mt-1">Driver Position Application</p>
          <p className="text-sm text-gray-400 mt-1">Westbury, NY</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="first_name"
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                  className="input"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="last_name"
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                  className="input"
                  placeholder="Smith"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                className="input"
                placeholder="(516) 555-1234"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="input"
                placeholder="john@example.com"
              />
            </div>

            {/* License */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Do you have a valid NY driver&rsquo;s license? <span className="text-red-500">*</span>
              </legend>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="has_license"
                    value="true"
                    checked={form.has_license === 'true'}
                    onChange={handleChange}
                    required
                    className="text-blue-600"
                  />
                  <span>Yes</span>
                </label>
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="has_license"
                    value="false"
                    checked={form.has_license === 'false'}
                    onChange={handleChange}
                    className="text-blue-600"
                  />
                  <span>No</span>
                </label>
              </div>
            </fieldset>

            {/* CDL License */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Do you have a CDL License?
              </legend>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="has_cdl_license"
                    value="true"
                    checked={form.has_cdl_license === 'true'}
                    onChange={handleChange}
                    className="text-blue-600"
                  />
                  <span>Yes</span>
                </label>
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="has_cdl_license"
                    value="false"
                    checked={form.has_cdl_license === 'false'}
                    onChange={handleChange}
                    className="text-blue-600"
                  />
                  <span>No</span>
                </label>
              </div>
            </fieldset>

            {/* Available start */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Available to start within 2 weeks?
              </legend>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="available_start"
                    value="within_2_weeks"
                    checked={form.available_start === 'within_2_weeks'}
                    onChange={handleChange}
                    className="text-blue-600"
                  />
                  <span>Yes</span>
                </label>
                <label className="touch-target flex items-center gap-2 cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                  <input
                    type="radio"
                    name="available_start"
                    value="more_than_2_weeks"
                    checked={form.available_start === 'more_than_2_weeks'}
                    onChange={handleChange}
                    className="text-blue-600"
                  />
                  <span>No</span>
                </label>
              </div>
            </fieldset>

            {/* Experience */}
            <div>
              <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                Any driving or transportation experience?
              </label>
              <textarea
                id="experience"
                name="experience"
                value={form.experience}
                onChange={handleChange}
                rows={3}
                className="input resize-none"
                placeholder="e.g., Uber driver for 2 years, school bus driver, etc."
              />
            </div>

            {/* Source */}
            <div>
              <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
                How did you hear about us?
              </label>
              <select
                id="source"
                name="source"
                value={form.source}
                onChange={handleChange}
                className="input bg-white"
              >
                <option value="">Select...</option>
                <option value="indeed">Indeed</option>
                <option value="craigslist">Craigslist</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* SMS Consent */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="sms_consent"
                  checked={form.sms_consent}
                  onChange={handleChange}
                  required
                  className="mt-1 h-5 w-5 text-blue-600"
                />
                <span className="text-sm text-gray-600">
                  I agree to receive text messages about my application from 1st Choice Ambulette Service.
                  Msg & data rates may apply. Reply STOP to opt out at any time.
                  <span className="text-red-500"> *</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !form.sms_consent}
              className="touch-target w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-3 px-4 rounded-lg transition-colors text-base focus-ring"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          1st Choice Ambulette Service · Westbury, NY · hiring.factrans.com
        </p>
      </div>
    </main>
  );
}
